'use client';

import { useMemo, useRef, useEffect, useState } from 'react';
import {
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Tooltip,
} from 'recharts';
import { Download, Share2, RefreshCw, BookOpen, Loader2 } from 'lucide-react';
import { useTranslations, useLocale } from 'next-intl';
import { LoveLanguage } from '@/types';
import { useNavigation } from '@/contexts/navigation-context';
import { ButtonLoader } from '@/components/ui/button-loader';
import { useLocalStorage } from '@/hooks/use-local-storage';
import {
  detectTopTie,
  getTieQuoteReferences,
  getSingleLanguageVerse,
  TieQuote,
} from '@/lib/tie-quotes';
import { fetchMultipleVerses, FetchedVerse } from '@/lib/bible-api';
import { getCachedVerse } from '@/hooks/use-bible-prefetch';

const STORAGE_KEY_ANSWERS = 'love_languages_answers';

export default function ResultsClient() {
  const t = useTranslations();
  const locale = useLocale();
  const { navigateTo, isNavigating } = useNavigation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isLightTheme, setIsLightTheme] = useState(true);

  const [storedAnswers, , isHydrated] = useLocalStorage<Record<number, LoveLanguage>>(STORAGE_KEY_ANSWERS, {});

  useEffect(() => {
    const updateTheme = () => {
      setIsLightTheme(!document.documentElement.classList.contains('dark'));
    };
    updateTheme();
    const observer = new MutationObserver(updateTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    return () => observer.disconnect();
  }, []);

  const scores = useMemo(() => {
    const counts: Record<LoveLanguage, number> = {
      [LoveLanguage.A]: 0,
      [LoveLanguage.B]: 0,
      [LoveLanguage.C]: 0,
      [LoveLanguage.D]: 0,
      [LoveLanguage.E]: 0,
    };

    Object.values(storedAnswers).forEach((val) => {
      const lang = val as LoveLanguage;
      if (counts[lang] !== undefined) {
        counts[lang]++;
      }
    });

    return counts;
  }, [storedAnswers]);

  const chartData = Object.keys(LoveLanguage).map((key) => {
    const langKey = key as LoveLanguage;
    return {
      subject: key,
      value: scores[langKey],
      fullMark: 12,
      langKey: langKey,
    };
  });

  const rankedLanguages = Object.entries(scores)
    .sort((a, b) => {
      const valA = a[1] as number;
      const valB = b[1] as number;
      return valB - valA;
    })
    .map((item, index) => ({
      rank: index + 1,
      lang: item[0] as LoveLanguage,
      score: item[1] as number,
    }));

  const primaryLanguage = rankedLanguages[0]?.lang || LoveLanguage.A;
  const secondaryLanguage = rankedLanguages[1]?.lang || LoveLanguage.B;

  const maxScore = rankedLanguages[0]?.score || 0;

  const tiedLanguages = useMemo(() => detectTopTie(scores), [scores]);
  const hasTie = tiedLanguages.length > 1;

  const [tieQuotes, setTieQuotes] = useState<TieQuote[]>([]);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [quotesError, setQuotesError] = useState<string | null>(null);

  useEffect(() => {
    const loadQuotes = async () => {
      setQuotesLoading(true);
      setQuotesError(null);

      try {
        let references: string[] = [];

        if (tiedLanguages.length > 0) {
          const quoteRefs = getTieQuoteReferences(tiedLanguages);
          references = quoteRefs.map((q) => q.reference);
        } else {
          const verseRef = getSingleLanguageVerse(primaryLanguage);
          if (verseRef) {
            references = [verseRef.reference];
          }
        }

        if (references.length === 0) {
          setTieQuotes([]);
          setQuotesLoading(false);
          return;
        }

        const baseLocale = locale.split('-')[0];

        const cachedQuotes: TieQuote[] = [];
        const missingRefs: string[] = [];

        for (const ref of references) {
          const cachedText = getCachedVerse(ref, baseLocale);
          if (cachedText) {
            cachedQuotes.push({ reference: ref, text: cachedText });
          } else {
            missingRefs.push(ref);
          }
        }

        if (missingRefs.length === 0) {
          setTieQuotes(cachedQuotes);
        } else {
          const fetchedVerses = await fetchMultipleVerses(missingRefs, baseLocale);

          if (fetchedVerses.length === 0 && baseLocale !== 'en') {
            const englishVerses = await fetchMultipleVerses(missingRefs, 'en');
            setTieQuotes([
              ...cachedQuotes,
              ...englishVerses.map((v) => ({
                reference: v.reference,
                text: v.text,
              })),
            ]);
          } else {
            setTieQuotes([
              ...cachedQuotes,
              ...fetchedVerses.map((v) => ({
                reference: v.reference,
                text: v.text,
              })),
            ]);
          }
        }
      } catch (error) {
        console.error('[Results] Failed to load Bible verses:', error);
        setQuotesError(t('error_loading_verses'));
      } finally {
        setQuotesLoading(false);
      }
    };

    loadQuotes();
  }, [tiedLanguages, primaryLanguage, locale, t]);

  const drawResultsCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    canvas.width = 1080;
    canvas.height = 1920;

    const colors = isLightTheme
      ? {
          bgBase: '#faf8f7',
          bgWarm: '#fdf2f8',
          primary: '#be123c',
          primaryLight: '#f43f5e',
          accent: '#d97706',
          textPrimary: '#1c1917',
          textSecondary: '#57534e',
          textMuted: '#78716c',
          textLight: '#a8a29e',
          chartFill: 'rgba(190, 18, 60, 0.25)',
          chartStroke: '#be123c',
          gridStroke: 'rgba(0, 0, 0, 0.08)',
          cardBg: 'rgba(255, 255, 255, 0.7)',
          cardBorder: 'rgba(190, 18, 60, 0.15)',
        }
      : {
          bgBase: '#0c0a09',
          bgWarm: '#1c1917',
          primary: '#f43f5e',
          primaryLight: '#fb7185',
          accent: '#fbbf24',
          textPrimary: '#fafaf9',
          textSecondary: '#d6d3d1',
          textMuted: '#a8a29e',
          textLight: '#78716c',
          chartFill: 'rgba(244, 63, 94, 0.3)',
          chartStroke: '#f43f5e',
          gridStroke: 'rgba(255, 255, 255, 0.1)',
          cardBg: 'rgba(28, 25, 23, 0.8)',
          cardBorder: 'rgba(244, 63, 94, 0.2)',
        };

    ctx.fillStyle = colors.bgBase;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const meshGradient1 = ctx.createRadialGradient(200, 300, 0, 200, 300, 500);
    meshGradient1.addColorStop(0, isLightTheme ? 'rgba(190, 18, 60, 0.08)' : 'rgba(244, 63, 94, 0.12)');
    meshGradient1.addColorStop(1, 'transparent');
    ctx.fillStyle = meshGradient1;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const meshGradient2 = ctx.createRadialGradient(880, 200, 0, 880, 200, 400);
    meshGradient2.addColorStop(0, isLightTheme ? 'rgba(217, 119, 6, 0.06)' : 'rgba(251, 191, 36, 0.08)');
    meshGradient2.addColorStop(1, 'transparent');
    ctx.fillStyle = meshGradient2;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const meshGradient3 = ctx.createRadialGradient(540, 1500, 0, 540, 1500, 500);
    meshGradient3.addColorStop(0, isLightTheme ? 'rgba(190, 18, 60, 0.05)' : 'rgba(244, 63, 94, 0.08)');
    meshGradient3.addColorStop(1, 'transparent');
    ctx.fillStyle = meshGradient3;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.fillStyle = colors.textMuted;
    ctx.font = '600 26px "Source Sans 3", Inter, sans-serif';
    if (locale === 'ar') ctx.font = '600 26px "Noto Sans Arabic", sans-serif';
    ctx.textAlign = 'center';
    ctx.letterSpacing = '4px';
    const subTitle = t('primary_lang_label').toUpperCase();
    ctx.fillText(subTitle, canvas.width / 2, 120);

    const titleGradient = ctx.createLinearGradient(200, 160, 880, 220);
    titleGradient.addColorStop(0, colors.primary);
    titleGradient.addColorStop(1, colors.primaryLight);
    ctx.fillStyle = titleGradient;
    ctx.font = '700 72px "Playfair Display", Georgia, serif';
    if (locale === 'ar') ctx.font = '700 72px "Noto Sans Arabic", sans-serif';
    const langTitle = t(`lang_${primaryLanguage}`);
    ctx.fillText(langTitle, canvas.width / 2, 200);

    const fullQuote = t(`quote_${primaryLanguage}`);
    const quoteMatch = fullQuote.match(/^"?([^"—]+)"?\s*—?\s*(.*)$/);
    const quoteText = quoteMatch ? `"${quoteMatch[1].trim()}"` : `"${fullQuote}"`;
    const quoteReference = quoteMatch && quoteMatch[2] ? quoteMatch[2].trim() : '';

    ctx.fillStyle = colors.textSecondary;
    ctx.font = 'italic 30px "Playfair Display", Georgia, serif';
    if (locale === 'ar') ctx.font = '30px "Noto Sans Arabic", sans-serif';
    const quoteEndY = wrapText(ctx, quoteText, canvas.width / 2, 280, 900, 40);

    if (quoteReference) {
      ctx.fillStyle = colors.primary;
      ctx.font = '600 24px "Source Sans 3", Inter, sans-serif';
      if (locale === 'ar') ctx.font = '600 24px "Noto Sans Arabic", sans-serif';
      ctx.fillText(`— ${quoteReference}`, canvas.width / 2, quoteEndY + 35);
    }

    const centerX = canvas.width / 2;
    const centerY = 680;
    const radius = 220;

    drawPentagonGrid(ctx, centerX, centerY, radius, 5, colors.gridStroke);

    const keys = [
      LoveLanguage.A,
      LoveLanguage.B,
      LoveLanguage.C,
      LoveLanguage.D,
      LoveLanguage.E,
    ];
    const points = keys.map((key, i) => {
      const angle = (Math.PI * 2 * i) / 5 - Math.PI / 2;
      const val = scores[key];
      const r = (val / 12) * radius;
      return {
        x: centerX + r * Math.cos(angle),
        y: centerY + r * Math.sin(angle),
      };
    });

    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    points.forEach((p) => ctx.lineTo(p.x, p.y));
    ctx.closePath();
    ctx.fillStyle = colors.chartFill;
    ctx.fill();

    const chartGlow = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
    chartGlow.addColorStop(0, isLightTheme ? 'rgba(190, 18, 60, 0.15)' : 'rgba(244, 63, 94, 0.2)');
    chartGlow.addColorStop(1, 'transparent');
    ctx.fillStyle = chartGlow;
    ctx.fill();

    ctx.strokeStyle = colors.chartStroke;
    ctx.lineWidth = 3;
    ctx.stroke();

    points.forEach((p) => {
      ctx.beginPath();
      ctx.arc(p.x, p.y, 6, 0, Math.PI * 2);
      ctx.fillStyle = colors.primary;
      ctx.fill();
      ctx.strokeStyle = isLightTheme ? '#fff' : colors.bgBase;
      ctx.lineWidth = 2;
      ctx.stroke();
    });

    keys.forEach((key, i) => {
      const angle = (Math.PI * 2 * i) / 5 - Math.PI / 2;
      const labelR = radius + 50;
      const x = centerX + labelR * Math.cos(angle);
      const y = centerY + labelR * Math.sin(angle);
      
      const isPrimary = key === primaryLanguage;
      ctx.fillStyle = isPrimary ? colors.primary : colors.textMuted;
      ctx.font = isPrimary 
        ? '700 28px "Source Sans 3", Inter, sans-serif'
        : '600 24px "Source Sans 3", Inter, sans-serif';
      if (locale === 'ar') ctx.font = isPrimary ? '700 28px "Noto Sans Arabic"' : '600 24px "Noto Sans Arabic"';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(key, x, y);
    });

    const cardStartY = 1050;
    const cardHeight = 72;
    const cardGap = 14;
    const cardWidth = 460;
    const cardRadius = 18;

    rankedLanguages.slice(0, 5).forEach((item, i) => {
      const isLeft = i % 2 === 0;
      const row = Math.floor(i / 2);
      const x = isLeft ? 70 : canvas.width - cardWidth - 70;
      const y = cardStartY + row * (cardHeight + cardGap);
      
      const isPrimary = item.lang === primaryLanguage;

      ctx.beginPath();
      ctx.roundRect(x, y, cardWidth, cardHeight, cardRadius);
      ctx.fillStyle = colors.cardBg;
      ctx.fill();
      
      if (isPrimary) {
        ctx.strokeStyle = colors.primary;
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      ctx.beginPath();
      ctx.roundRect(x + 12, y + 12, 48, 48, 12);
      ctx.fillStyle = isPrimary ? colors.primary : (isLightTheme ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.08)');
      ctx.fill();

      ctx.fillStyle = isPrimary ? '#fff' : colors.textMuted;
      ctx.font = '700 20px "Source Sans 3", Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(item.lang, x + 12 + 24, y + 12 + 24);

      ctx.fillStyle = isPrimary ? colors.primary : colors.textPrimary;
      ctx.font = isPrimary 
        ? '600 24px "Source Sans 3", Inter, sans-serif'
        : '500 22px "Source Sans 3", Inter, sans-serif';
      if (locale === 'ar') ctx.font = isPrimary ? '600 24px "Noto Sans Arabic"' : '500 22px "Noto Sans Arabic"';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      const langName = t(`lang_${item.lang}`);
      const maxTextWidth = cardWidth - 130;
      let displayName = langName;
      if (ctx.measureText(langName).width > maxTextWidth) {
        while (ctx.measureText(displayName + '...').width > maxTextWidth && displayName.length > 0) {
          displayName = displayName.slice(0, -1);
        }
        displayName += '...';
      }
      ctx.fillText(displayName, x + 72, y + cardHeight / 2);

      ctx.fillStyle = isPrimary ? colors.primary : colors.textMuted;
      ctx.font = '700 22px "Source Sans 3", Inter, sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText(`${item.score}`, x + cardWidth - 20, y + cardHeight / 2);
    });

    const separatorY = 1340;
    const separatorWidth = 200;
    
    ctx.beginPath();
    ctx.moveTo((canvas.width - separatorWidth) / 2, separatorY);
    ctx.lineTo((canvas.width + separatorWidth) / 2, separatorY);
    ctx.strokeStyle = isLightTheme ? 'rgba(190, 18, 60, 0.2)' : 'rgba(244, 63, 94, 0.3)';
    ctx.lineWidth = 1;
    ctx.stroke();

    const diamondSize = 6;
    ctx.beginPath();
    ctx.moveTo(canvas.width / 2, separatorY - diamondSize);
    ctx.lineTo(canvas.width / 2 + diamondSize, separatorY);
    ctx.lineTo(canvas.width / 2, separatorY + diamondSize);
    ctx.lineTo(canvas.width / 2 - diamondSize, separatorY);
    ctx.closePath();
    ctx.fillStyle = colors.primary;
    ctx.fill();

    const symbolsY = 1410;
    
    ctx.fillStyle = colors.textMuted;
    ctx.font = '600 20px "Source Sans 3", Inter, sans-serif';
    if (locale === 'ar') ctx.font = '600 20px "Noto Sans Arabic", sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(t('info_symbols_title').toUpperCase(), canvas.width / 2, symbolsY);

    const symbolsRaw = t.raw(`info_symbols_${primaryLanguage}`) as string[];
    const symbols = Array.isArray(symbolsRaw) ? symbolsRaw : [];
    
    if (symbols.length > 0) {
      const pillHeight = 52;
      const pillPadding = 24;
      const pillGap = 16;
      const pillRadius = 26;
      
      ctx.font = '500 22px "Source Sans 3", Inter, sans-serif';
      
      const pillWidths = symbols.map(symbol => {
        const text = symbol.replace(/^\p{Emoji}\s*/u, '').trim();
        return ctx.measureText(text).width + pillPadding * 2 + 36;
      });
      
      const row1Symbols = symbols.slice(0, 3);
      const row2Symbols = symbols.slice(3, 5);
      
      const row1Width = pillWidths.slice(0, 3).reduce((a, b) => a + b, 0) + pillGap * (row1Symbols.length - 1);
      const row2Width = pillWidths.slice(3, 5).reduce((a, b) => a + b, 0) + pillGap * (row2Symbols.length - 1);
      
      let currentX = (canvas.width - row1Width) / 2;
      const row1Y = symbolsY + 35;
      
      row1Symbols.forEach((symbol, i) => {
        const emojiMatch = symbol.match(/^(\p{Emoji})\s*/u);
        const emoji = emojiMatch ? emojiMatch[1] : '';
        const text = symbol.replace(/^\p{Emoji}\s*/u, '').trim();
        const pillWidth = pillWidths[i];
        
        ctx.beginPath();
        ctx.roundRect(currentX, row1Y, pillWidth, pillHeight, pillRadius);
        ctx.fillStyle = isLightTheme ? 'rgba(190, 18, 60, 0.08)' : 'rgba(244, 63, 94, 0.15)';
        ctx.fill();
        ctx.strokeStyle = isLightTheme ? 'rgba(190, 18, 60, 0.2)' : 'rgba(244, 63, 94, 0.3)';
        ctx.lineWidth = 1;
        ctx.stroke();
        
        ctx.font = '400 24px "Apple Color Emoji", "Segoe UI Emoji", sans-serif';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = colors.textPrimary;
        ctx.fillText(emoji, currentX + pillPadding, row1Y + pillHeight / 2);
        
        ctx.font = '500 20px "Source Sans 3", Inter, sans-serif';
        if (locale === 'ar') ctx.font = '500 20px "Noto Sans Arabic", sans-serif';
        ctx.fillStyle = colors.textSecondary;
        ctx.fillText(text, currentX + pillPadding + 32, row1Y + pillHeight / 2);
        
        currentX += pillWidth + pillGap;
      });
      
      if (row2Symbols.length > 0) {
        currentX = (canvas.width - row2Width) / 2;
        const row2Y = row1Y + pillHeight + pillGap;
        
        row2Symbols.forEach((symbol, i) => {
          const emojiMatch = symbol.match(/^(\p{Emoji})\s*/u);
          const emoji = emojiMatch ? emojiMatch[1] : '';
          const text = symbol.replace(/^\p{Emoji}\s*/u, '').trim();
          const pillWidth = pillWidths[i + 3];
          
          ctx.beginPath();
          ctx.roundRect(currentX, row2Y, pillWidth, pillHeight, pillRadius);
          ctx.fillStyle = isLightTheme ? 'rgba(190, 18, 60, 0.08)' : 'rgba(244, 63, 94, 0.15)';
          ctx.fill();
          ctx.strokeStyle = isLightTheme ? 'rgba(190, 18, 60, 0.2)' : 'rgba(244, 63, 94, 0.3)';
          ctx.lineWidth = 1;
          ctx.stroke();
          
          ctx.font = '400 24px "Apple Color Emoji", "Segoe UI Emoji", sans-serif';
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          ctx.fillStyle = colors.textPrimary;
          ctx.fillText(emoji, currentX + pillPadding, row2Y + pillHeight / 2);
          
          ctx.font = '500 20px "Source Sans 3", Inter, sans-serif';
          if (locale === 'ar') ctx.font = '500 20px "Noto Sans Arabic", sans-serif';
          ctx.fillStyle = colors.textSecondary;
          ctx.fillText(text, currentX + pillPadding + 32, row2Y + pillHeight / 2);
          
          currentX += pillWidth + pillGap;
        });
      }
    }

    const footerY = canvas.height - 140;
    
    ctx.beginPath();
    ctx.moveTo(200, footerY);
    ctx.lineTo(canvas.width - 200, footerY);
    ctx.strokeStyle = colors.gridStroke;
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.fillStyle = colors.textMuted;
    ctx.font = '500 28px "Source Sans 3", Inter, sans-serif';
    if (locale === 'ar') ctx.font = '500 28px "Noto Sans Arabic", sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('lovebucket.xyz', canvas.width / 2, footerY + 60);

    return canvas;
  };

  const handleShare = async () => {
    const infoUrl = `${window.location.origin}/${locale}/guide/${primaryLanguage}`;
    const homeUrl = `${window.location.origin}/${locale}`;
    const shareText = `${t('share_text', {
      lang: t(`lang_${primaryLanguage}`),
    })} ${infoUrl} - ${t('share_cta', { url: homeUrl })}`;
    const shareData = {
      title: t('share_title'),
      text: shareText,
    };

    const canvas = drawResultsCanvas();
    const blob = canvas
      ? await new Promise<Blob | null>((resolve) => {
          canvas.toBlob((generated) => resolve(generated), 'image/png');
        })
      : null;

    const file = blob
      ? new File([blob], 'love-languages-results.png', { type: 'image/png' })
      : null;

    if (
      file &&
      navigator.canShare &&
      navigator.canShare({ files: [file] }) &&
      navigator.share
    ) {
      try {
        await navigator.share({
          files: [file],
          title: shareData.title,
          text: shareData.text,
        });
        return;
      } catch (err) {
        console.error('Error sharing', err);
      }
    }

    if (file && navigator.clipboard && 'write' in navigator.clipboard) {
      try {
        const item = new ClipboardItem({
          [file.type]: file,
          'text/plain': new Blob([shareText], { type: 'text/plain' }),
        });
        await navigator.clipboard.write([item]);
        alert(t('clipboard_success'));
        return;
      } catch (err) {
        console.error('Error copying image to clipboard', err);
      }
    }

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch (err) {
        console.error('Error sharing', err);
      }
    }

    try {
      await navigator.clipboard.writeText(shareText);
      alert(t('clipboard_success'));
    } catch (err) {
      console.error('Error copying to clipboard', err);
    }
  };

  const handleDownload = () => {
    const canvas = drawResultsCanvas();
    if (!canvas) return;

    const link = document.createElement('a');
    link.download = `${t('download_filename')}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const handleReset = () => {
    localStorage.removeItem(STORAGE_KEY_ANSWERS);
    navigateTo('/');
  };

  const handleOpenInfo = () => {
    navigateTo(`/guide/${primaryLanguage}`);
  };

  return (
    <div
      className="w-full max-w-4xl mx-auto px-4 py-10 relative"
      data-testid="results-component"
    >
      <h1 className="sr-only">{t('results_page_title')}</h1>
      <div className="absolute inset-0 bg-mesh-gradient pointer-events-none" />
      <div className="relative z-10 grid md:grid-cols-2 gap-8 items-start">
        <div
          className="bg-card border border-border card-elevated rounded-3xl p-7 flex flex-col items-center opacity-0 animate-reveal-up"
          data-testid="results-chart-section"
        >
          <h2
            className="font-display text-xl font-semibold mb-6 text-foreground"
            data-testid="results-chart-title"
          >
            {t('results_chart_title')}
          </h2>
          <div className="h-[300px] w-full" dir="ltr" data-testid="results-radar-chart">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart cx="50%" cy="50%" outerRadius="80%" data={chartData}>
                <PolarGrid stroke="#94a3b8" strokeOpacity={0.5} />
                <PolarAngleAxis
                  dataKey="subject"
                  tick={{
                    fill: isLightTheme ? '#000000' : '#ffffff',
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                />
                <PolarRadiusAxis
                  angle={30}
                  domain={[0, 12]}
                  tick={false}
                  axisLine={false}
                />
                {Object.values(LoveLanguage).map((lang) => (
                  <Radar
                    key={lang}
                    name={lang}
                    dataKey="value"
                    stroke="#e11d48"
                    fill="#e11d48"
                    fillOpacity={0.45}
                    isAnimationActive={true}
                  />
                ))}
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1f2937',
                    color: '#f1f5f9',
                    borderRadius: '8px',
                    border: '1px solid #4b5563',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.3)',
                  }}
                  content={({ active, payload }) => {
                    if (active && payload && payload.length > 0) {
                      const data = payload[0].payload;
                      return (
                        <div className="bg-gray-800 text-slate-50 p-3 rounded-lg border border-gray-700">
                          <p className="font-semibold">{t(`lang_${data.langKey}`)}</p>
                          <p className="text-sm">
                            {data.subject}: {data.value}
                          </p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>

          <div className="w-full mt-8 space-y-2.5" data-testid="results-ranked-list">
            {rankedLanguages.map(({ rank, lang, score }) => {
              const isTopRanked = score === maxScore;
              return (
                <div
                  key={lang}
                  className={`flex items-center rounded-2xl transition-all ${
                    isTopRanked
                      ? 'p-4 bg-primary/8 border-2 border-primary card-glow'
                      : 'p-3.5 bg-secondary/60 border border-border'
                  }`}
                  data-testid={`results-rank-item-${lang}`}
                >
                  <span
                    className={`font-bold text-muted-foreground min-w-8 ${
                      isTopRanked ? 'text-base' : 'text-sm'
                    }`}
                  >
                    {rank}º
                  </span>
                  <span
                    className={`font-display font-semibold text-primary mx-3 ${
                      isTopRanked ? 'text-lg' : ''
                    }`}
                  >
                    {lang}
                  </span>
                  <span
                    className={`flex-1 text-foreground font-medium ${
                      isTopRanked ? 'text-base' : 'text-sm'
                    }`}
                  >
                    {t(`lang_${lang}`)}
                  </span>
                  <span
                    className={`text-muted-foreground font-semibold ${
                      isTopRanked ? 'text-base' : 'text-sm'
                    }`}
                  >
                    [{score}]
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div
          className="space-y-6 opacity-0 animate-reveal-up stagger-1"
          data-testid="results-text-section"
        >
          {hasTie ? (
            <div
              className="bg-card border border-border card-elevated rounded-3xl p-7 rtl:border-r-4 rtl:border-l-0 ltr:border-l-4 ltr:border-r-0 border-primary"
              data-testid="results-tie-card"
            >
              <span
                className="block text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-3"
                data-testid="results-tie-label"
              >
                {t('tie_label')}
              </span>
              <h2
                className="font-display text-3xl md:text-4xl font-semibold text-gradient mb-4"
                data-testid="results-tie-languages"
              >
                {tiedLanguages.map((lang) => t(`lang_${lang}`)).join(' + ')}
              </h2>
              <p className="text-foreground/90 leading-relaxed mb-5">{t('tie_desc')}</p>

              {quotesLoading && (
                <div
                  className="flex items-center justify-center py-6"
                  data-testid="results-verses-loading"
                >
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  <span className="ml-2 text-muted-foreground">{t('loading_verses')}</span>
                </div>
              )}

              {quotesError && !quotesLoading && (
                <div
                  className="bg-destructive/10 border border-destructive/30 p-4 rounded-xl text-destructive text-sm"
                  data-testid="results-verses-error"
                >
                  {quotesError}
                </div>
              )}

              {tieQuotes.length > 0 && !quotesLoading && !quotesError && (
                <div className="space-y-3" data-testid="results-bible-verses">
                  {tieQuotes.map((quote, index) => (
                    <div
                      key={index}
                      className="bg-secondary/60 p-5 rounded-2xl italic text-muted-foreground rtl:border-r-3 rtl:border-l-0 ltr:border-l-3 ltr:border-r-0 border-primary/40"
                      data-testid={`results-verse-${index}`}
                    >
                      <p className="mb-2 leading-relaxed">{quote.text}</p>
                      <p className="text-xs font-semibold text-primary not-italic">
                        — {quote.reference}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div
              className="bg-card border border-border card-elevated rounded-3xl p-7 rtl:border-r-4 rtl:border-l-0 ltr:border-l-4 ltr:border-r-0 border-primary"
              data-testid="results-primary-card"
            >
              <span
                className="block text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-3"
                data-testid="results-primary-label"
              >
                {t('primary_lang_label')}
              </span>
              <h2
                className="font-display text-3xl md:text-4xl font-semibold text-gradient mb-4"
                data-testid="results-primary-title"
              >
                {t(`lang_${primaryLanguage}`)}
              </h2>
              <p
                className="text-foreground/90 leading-relaxed mb-5"
                data-testid="results-primary-desc"
              >
                {t(`desc_${primaryLanguage}`)}
              </p>

              {quotesLoading && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-primary" />
                  <span className="ml-2 text-sm text-muted-foreground">
                    {t('loading_verses')}
                  </span>
                </div>
              )}

              {quotesError && !quotesLoading && (
                <div className="bg-destructive/10 border border-destructive/30 p-3 rounded-xl text-destructive text-sm">
                  {quotesError}
                </div>
              )}

              {!quotesLoading && !quotesError && (
                <div
                  className="bg-secondary/60 p-5 rounded-2xl italic text-muted-foreground rtl:border-r-3 rtl:border-l-0 ltr:border-l-3 ltr:border-r-0 border-primary/40"
                  data-testid="results-primary-quote"
                >
                  {tieQuotes.length > 0 ? (
                    <>
                      <p className="mb-2 leading-relaxed">{tieQuotes[0].text}</p>
                      <p className="text-xs font-semibold text-primary not-italic">
                        — {tieQuotes[0].reference}
                      </p>
                    </>
                  ) : (
                    <>{t(`quote_${primaryLanguage}`)}</>
                  )}
                </div>
              )}
            </div>
          )}

          {!hasTie && scores[primaryLanguage] === scores[secondaryLanguage] && (
            <div
              className="bg-card border border-border card-elevated rounded-3xl p-6"
              data-testid="results-secondary-card"
            >
              <span
                className="block text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-2"
                data-testid="results-secondary-label"
              >
                {t('secondary_lang_label')}
              </span>
              <h3
                className="font-display text-2xl font-semibold text-foreground mb-2"
                data-testid="results-secondary-title"
              >
                {t(`lang_${secondaryLanguage}`)}
              </h3>
              <p
                className="text-muted-foreground text-sm"
                data-testid="results-secondary-desc"
              >
                {t('secondary_lang_desc')}
              </p>
            </div>
          )}

          <button
            onClick={handleOpenInfo}
            disabled={isNavigating}
            className="w-full btn-primary flex items-center justify-center gap-2.5 text-primary-foreground h-14 rounded-2xl font-semibold text-base transition-all cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed"
            data-testid="results-read-more"
          >
            {isNavigating ? (
              <ButtonLoader size="md" />
            ) : (
              <>
                <BookOpen className="w-5 h-5" />
                {t('btn_read_more', { lang: t(`lang_${primaryLanguage}`) })}
              </>
            )}
          </button>

          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={handleDownload}
              className="flex-1 flex items-center justify-center gap-2 bg-secondary hover:bg-secondary/80 text-secondary-foreground h-12 rounded-xl font-semibold transition-all border border-border cursor-pointer"
              data-testid="results-download"
            >
              <Download className="w-4 h-4" /> {t('download_btn')}
            </button>
            <button
              onClick={handleShare}
              className="flex-1 flex items-center justify-center gap-2 bg-secondary hover:bg-secondary/80 text-secondary-foreground h-12 rounded-xl font-semibold transition-all border border-border cursor-pointer"
              data-testid="results-share"
            >
              <Share2 className="w-4 h-4" /> {t('share_btn')}
            </button>
          </div>

          <button
            onClick={handleReset}
            className="w-full flex items-center justify-center gap-2 text-muted-foreground hover:text-foreground text-sm py-3 transition-colors cursor-pointer"
            data-testid="results-reset"
          >
            <RefreshCw className="w-4 h-4" /> {t('reset_btn')}
          </button>
        </div>
      </div>

      <div className="mt-8 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 border border-amber-200 dark:border-amber-800 rounded-2xl p-6 max-w-4xl mx-auto">
        <div className="flex gap-3">
          <span className="text-2xl">💡</span>
          <p className="text-sm text-amber-950 dark:text-amber-100 leading-relaxed">
            {t('did_you_know')}
          </p>
        </div>
      </div>

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number
): number {
  const words = text.split(' ');
  let line = '';
  let currentY = y;

  for (let n = 0; n < words.length; n++) {
    const testLine = line + words[n] + ' ';
    const metrics = ctx.measureText(testLine);
    const testWidth = metrics.width;
    if (testWidth > maxWidth && n > 0) {
      ctx.fillText(line, x, currentY);
      line = words[n] + ' ';
      currentY += lineHeight;
    } else {
      line = testLine;
    }
  }
  ctx.fillText(line, x, currentY);
  return currentY;
}

function drawPentagonGrid(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  sides: number,
  strokeColor: string = '#e5e7eb'
) {
  ctx.strokeStyle = strokeColor;
  ctx.lineWidth = 2;

  for (let j = 1; j <= 4; j++) {
    const curR = (r / 4) * j;
    ctx.beginPath();
    for (let i = 0; i < sides; i++) {
      const angle = (Math.PI * 2 * i) / sides - Math.PI / 2;
      const px = x + curR * Math.cos(angle);
      const py = y + curR * Math.sin(angle);
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.stroke();
  }

  ctx.beginPath();
  for (let i = 0; i < sides; i++) {
    const angle = (Math.PI * 2 * i) / sides - Math.PI / 2;
    ctx.moveTo(x, y);
    ctx.lineTo(x + r * Math.cos(angle), y + r * Math.sin(angle));
  }
  ctx.stroke();
}
